import React, { createContext, useContext, useEffect, useState, useRef, useMemo, ReactNode } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../hooks/Keycloak/useAuthenticatedFetchWithWorkspace';

export interface RefDiscountType {
  id: number;
  label: string;
}

interface MarketingDataContextType {
  refDiscountTypes: RefDiscountType[];
  loading: boolean;
  error: string | null;
}

const MarketingDataContext = createContext<MarketingDataContextType | undefined>(undefined);

export const useMarketingData = (): MarketingDataContextType => {
  const ctx = useContext(MarketingDataContext);
  if (!ctx) throw new Error('useMarketingData must be used within MarketingDataProvider');
  return ctx;
};

const toList = <T,>(result: unknown): T[] => {
  const r = result as Record<string, unknown>;
  if (Array.isArray(result)) return result as T[];
  if (Array.isArray(r?.['hydra:member'])) return r['hydra:member'] as T[];
  if (Array.isArray(r?.data)) return r.data as T[];
  return [];
};

export const MarketingDataProvider = ({ children }: { children: ReactNode }) => {
  const { authFetch, isReady, isLoading: workspaceLoading, workspaceId } = useAuthenticatedFetchWithWorkspace();

  const [refDiscountTypes, setRefDiscountTypes] = useState<RefDiscountType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadedForWorkspace = useRef<string | null>(null);

  useEffect(() => {
    if (workspaceLoading || !isReady || !workspaceId) {
      if (!refDiscountTypes.length) setLoading(true);
      return;
    }

    if (loadedForWorkspace.current === workspaceId) return;

    if (loadedForWorkspace.current !== null) {
      setRefDiscountTypes([]);
    }
    setLoading(true);
    setError(null);

    authFetch('/ref_discount_type_inputs', { method: 'GET' })
      .then(result => {
        setRefDiscountTypes(toList<RefDiscountType>(result));
        loadedForWorkspace.current = workspaceId;
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Erreur de chargement'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, workspaceLoading, workspaceId]);

  const value = useMemo(
    () => ({ refDiscountTypes, loading, error }),
    [refDiscountTypes, loading, error],
  );

  return (
    <MarketingDataContext.Provider value={value}>
      {children}
    </MarketingDataContext.Provider>
  );
};
